import {
  WBBox,
  WBButton,
  WBDialog,
  WBDialogContent,
  WBFlex,
  WBForm,
  WBIconButton,
  WBTelInputV2,
  WBTextFieldV2,
  WBTypography,
  useSnackbar,
} from '@admiin-com/ds-web';
import { Entity } from '@admiin-com/ds-graphql';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { REGEX } from '@admiin-com/ds-common';
import { DialogTitle } from '@mui/material';
import { matchIsValidTel } from 'mui-tel-input';
import { useAddClientOwner } from './useAddClientOwner';

export interface AddClientOwnerModalProps {
  open: boolean;
  onClose: () => void;
  entity: Entity;
  onSuccess?: () => void;
}

interface AddClientOwnerFormData {
  email: string;
  firstName: string;
  lastName: string;
  mobile?: string;
}

export function AddClientOwnerModal({
  open,
  onClose,
  entity,
  onSuccess,
}: AddClientOwnerModalProps) {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const [loading, setLoading] = useState(false);
  const { addClientOwner } = useAddClientOwner();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<AddClientOwnerFormData>({
    defaultValues: {
      email: entity?.contact?.email || '',
      firstName: entity?.contact?.firstName || '',
      lastName: entity?.contact?.lastName || '',
      mobile: entity?.contact?.phone || '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: AddClientOwnerFormData) => {
    if (!entity?.id) {
      showSnackbar({
        message: t('entityRequired', {
          ns: 'errors',
          defaultValue: 'Entity is required',
        }),
        severity: 'error',
        horizontal: 'center',
        vertical: 'bottom',
      });
      return;
    }

    setLoading(true);
    try {
      await addClientOwner({
        entityId: entity.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        mobile: data.mobile || undefined,
      });

      showSnackbar({
        message: t('ownerAddedSuccess', {
          ns: 'clients',
          defaultValue: 'Client owner added successfully',
        }),
        severity: 'success',
        horizontal: 'center',
        vertical: 'bottom',
      });

      reset();
      onClose();
      onSuccess?.();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      console.error('Error adding client owner:', err);
      showSnackbar({
        message: t(errorMessage, { ns: 'errors' }),
        severity: 'error',
        horizontal: 'center',
        vertical: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <WBDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <WBFlex justifyContent="space-between" alignItems="center">
          <WBTypography variant="h5">
            {t('addOwnerTitle', {
              ns: 'clients',
              defaultValue: 'Add the entity owner',
            })}
          </WBTypography>
          <WBIconButton icon="Close" onClick={handleClose} />
        </WBFlex>
      </DialogTitle>
      <WBDialogContent>
        <WBBox>
          <WBForm onSubmit={handleSubmit(onSubmit)}>
            <WBFlex flexDirection="column" gap={2}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: t('firstNameRequired', { ns: 'common' }),
                }}
                render={({ field }) => (
                  <WBTextFieldV2
                    {...field}
                    label={t('firstName', { ns: 'common' })}
                    required
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message as string}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: t('lastNameRequired', { ns: 'common' }),
                }}
                render={({ field }) => (
                  <WBTextFieldV2
                    {...field}
                    label={t('lastName', { ns: 'common' })}
                    required
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message as string}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: t('emailRequired', { ns: 'common' }),
                  validate: (value: string) =>
                    REGEX.EMAIL.test(value) ||
                    t('invalidEmail', { ns: 'common' }),
                }}
                render={({ field }) => (
                  <WBTextFieldV2
                    {...field}
                    label={t('email', { ns: 'common' })}
                    type="email"
                    required
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message as string}
                  />
                )}
              />
              <Controller
                name="mobile"
                control={control}
                rules={{
                  validate: (value: string | undefined) =>
                    !value ||
                    value === '' ||
                    matchIsValidTel(value) ||
                    t('invalidMobile', { ns: 'common' }),
                }}
                render={({ field }) => (
                  <WBTelInputV2
                    {...field}
                    label={t('mobile', { ns: 'common' })}
                    fullWidth
                    defaultCountry="AU"
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message as string}
                  />
                )}
              />
              <WBFlex gap={2} justifyContent="flex-end" mt={2}>
                <WBButton
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                >
                  {t('cancel', { ns: 'common' })}
                </WBButton>
                <WBButton
                  type="submit"
                  variant="contained"
                  disabled={!isValid || loading}
                  loading={loading}
                >
                  {t('addOwner', {
                    ns: 'clients',
                    defaultValue: 'Add Owner',
                  })}
                </WBButton>
              </WBFlex>
            </WBFlex>
          </WBForm>
        </WBBox>
      </WBDialogContent>
    </WBDialog>
  );
}

export default AddClientOwnerModal;
